import React, { useRef, useState } from 'react';
import { Spinner, Typography } from '@zydalabs/zac-react';
import useTranslation from 'next-translate/useTranslation';
import { Icon } from '@zydalabs/storefront-components';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';

import config from 'common/config';
import { usePersistedCountryDetails } from 'common/hooks';
import { IcSearch } from 'common/icons';
import ServiceConfigs from 'service/config';
import { AutoCompleteInputWrapper, AutoCompleteItemWrapper, AutoCompleteListWrapper, SpinnerWrapper } from './style';

type MapTileTypes = {
  setCenter: (coords: [number, number]) => void;
};

const AutoComplete: React.FC<MapTileTypes> = ({ setCenter }) => {
  const [persistedCountryDetails] = usePersistedCountryDetails();
  const googleMapsApiKey = ServiceConfigs.getGoogleMapsApiKey();
  const [isSearching, setIsSearching] = useState(false);
  const { t, lang } = useTranslation('common');
  const inputRef = useRef();
  const multicountryEnabled = ServiceConfigs.getMulticountryEnabled();

  const { placePredictions, getPlacePredictions, isPlacePredictionsLoading } = usePlacesService({
    apiKey: googleMapsApiKey ?? config.common.googleMapKey,
    debounce: 800,
  });

  const onPlaceSelected = prediction => {
    const request = { placeId: prediction.place_id, fields: ['geometry'] };

    const placeService = new window.google.maps.places.PlacesService(inputRef.current);

    placeService.getDetails(request, async placeChosed => {
      setCenter([placeChosed.geometry.location.lat(), placeChosed.geometry.location.lng()]);
      setIsSearching(false);
    });
  };

  return (
    <AutoCompleteInputWrapper
      tabIndex={0}
      onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
        const { currentTarget } = event;
        // Give browser time to focus the next element
        requestAnimationFrame(() => {
          // Check if the new focused element is a child of the original container
          if (!currentTarget.contains(document.activeElement)) {
            setIsSearching(false);
          }
        });
      }}
    >
      <Icon icon={IcSearch} />
      <input
        ref={inputRef}
        placeholder={t('enterLocation')}
        onClick={() => setIsSearching(true)}
        onChange={evt => {
          getPlacePredictions({
            input: evt.target.value,
            language: lang,
            ...(multicountryEnabled ? {} : { componentRestrictions: { country: persistedCountryDetails.code } }),
          });
        }}
      />
      {placePredictions && placePredictions.length > 0 && isSearching && (
        <AutoCompleteListWrapper>
          {isPlacePredictionsLoading ? (
            <SpinnerWrapper>
              <Spinner />
            </SpinnerWrapper>
          ) : (
            placePredictions.map(prediction => (
              <AutoCompleteItemWrapper key={prediction.place_id} onClick={() => onPlaceSelected(prediction)} aria-hidden="true">
                <Typography variant="body16" cursor="pointer">
                  {prediction.description}
                </Typography>
              </AutoCompleteItemWrapper>
            ))
          )}
        </AutoCompleteListWrapper>
      )}
    </AutoCompleteInputWrapper>
  );
};

export default AutoComplete;
